import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {
  probePoseBenchmark,
  startPoseBenchmark,
  pollPoseBenchmark,
} from "../api/poseBenchmarkApi";
import "./BenchmarkVideoDialog.css";

const METRIC_ROWS = [
  { key: "process_s", label: "Processing time (s)" },
  { key: "wall_s", label: "Wall time (s)" },
  { key: "model_load_s", label: "Model load (s)" },
  { key: "decode_infer_s", label: "Decode + infer (s)" },
  { key: "pose2d_detect_fps", label: "detect_fps" },
  { key: "coverage_fps", label: "coverage_fps" },
  { key: "device", label: "Device" },
  { key: "forehand_count", label: "Forehand count" },
  { key: "stroke_count", label: "Stroke count" },
  { key: "essai_count", label: "Try count" },
  { key: "rally_count", label: "Rally count" },
  { key: "rally_number", label: "Rally number" },
  { key: "strokes_per_essai", label: "Strokes / try" },
  { key: "pct_strokes_in_rallies", label: "% strokes in rallies" },
  { key: "inter_stroke_interval_ms", label: "Inter-stroke interval (ms)" },
  { key: "amplitude_ms_avg", label: "Amplitude avg (ms)" },
  { key: "hit_rate_left", label: "Hit rate left" },
  { key: "hit_rate_right", label: "Hit rate right" },
];

const MODEL_ORDER = [
  { id: "mediapipe_full", label: "MediaPipe" },
  { id: "rtmpose_onnx", label: "RTMPose" },
  { id: "yolo11_pose", label: "YOLO11-Pose" },
];

function formatMetric(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return "—";
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
}

/**
 * Coach dialog: frozen-calib multi-model bench (MediaPipe + RTMPose + YOLO on GCP L4).
 */
export function BenchmarkVideoDialog({
  open,
  onClose,
  teId,
  videoUrl,
  messageId,
  videoLabel,
  t = (k, d) => d || k,
}) {
  const [phase, setPhase] = useState("probe"); // probe | ready | running | done | error
  const [probe, setProbe] = useState(null);
  const [bench, setBench] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [progressLabel, setProgressLabel] = useState("");
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    abortRef.current?.abort?.();
    abortRef.current = null;
    setPhase("probe");
    setProbe(null);
    setBench(null);
    setError(null);
    setProgress(null);
    setProgressLabel("");
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setPhase("probe");
        setError(null);
        const data = await probePoseBenchmark({
          teId,
          videoUrl,
          messageId,
        });
        if (cancelled) return;
        setProbe(data);
        setPhase(data.calibration_ready ? "ready" : "error");
        if (!data.calibration_ready) {
          setError(
            data.hint ||
              t(
                "benchmarkNeedCalib",
                "Calibrate this video first (table + players), run pose, then retry."
              )
          );
        }
      } catch (e) {
        if (cancelled) return;
        setPhase("error");
        setError(e.message || "Probe failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, teId, videoUrl, messageId, t, reset]);

  const run = useCallback(async () => {
    const ac = new AbortController();
    abortRef.current = ac;
    setPhase("running");
    setError(null);
    setProgress(0.02);
    setProgressLabel(t("benchmarkStarting", "Starting multi-model benchmark…"));
    try {
      const started = await startPoseBenchmark({
        teId,
        videoUrl,
        messageId,
        poseFps: 15,
      });
      const final = await pollPoseBenchmark(started.bench_id, {
        signal: ac.signal,
        onUpdate: (d) => {
          setBench(d);
          const models = d?.models || {};
          const ids = MODEL_ORDER.map((m) => m.id);
          let done = 0;
          let runningIdx = -1;
          let runningP = 0;
          ids.forEach((id, i) => {
            const s = models[id]?.status;
            if (s === "complete" || s === "failed") done += 1;
            else if (s === "running" || s === "processing" || s === "queued") {
              if (runningIdx < 0) {
                runningIdx = i;
                runningP = Number(models[id]?.progress) || 0;
              }
            }
          });
          const overall = (done + runningP) / ids.length;
          setProgress(overall);
          const label =
            runningIdx >= 0
              ? `${MODEL_ORDER[runningIdx].label} (${runningIdx + 1}/${ids.length})`
              : done === ids.length
                ? t("benchmarkFinishing", "Finishing…")
                : t("benchmarkRunningModels", "Running models…");
          setProgressLabel(label);
        },
      });
      setBench(final);
      setPhase(
        final.status === "failed"
          ? "error"
          : final.status === "partial"
            ? "done"
            : "done"
      );
      if (final.status === "failed") {
        setError(
          final.error ||
            final.models?.mediapipe_full?.error ||
            t("benchmarkFailed", "Benchmark failed")
        );
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      setPhase("error");
      setError(e.message || "Benchmark failed");
    }
  }, [teId, videoUrl, messageId, t]);

  const modelCols = useMemo(() => {
    const models = bench?.models || {};
    return MODEL_ORDER.map((m) => {
      const slot = models[m.id] || {};
      const probeModel = probe?.models?.find((x) => x.id === m.id);
      return {
        id: m.id,
        label: m.label,
        status: slot.status || probeModel?.status || "ready",
        metrics: slot.metrics,
        error: slot.error,
        device: slot.device || slot.metrics?.device,
      };
    });
  }, [bench, probe]);

  return (
    <Dialog
      open={open}
      onClose={phase === "running" ? undefined : onClose}
      maxWidth="md"
      fullWidth
      className="benchmark-video-dialog"
      // Above ExerciseResultDialog portal (z-index ~9999–12000); same as MoveEvaluationVideoDialog
      sx={{ zIndex: 13000 }}
    >
      <DialogTitle>
        {t("benchmarkVideoTitle", "Benchmark video")}
        {videoLabel ? (
          <span className="benchmark-video-dialog__subtitle">{videoLabel}</span>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {phase === "probe" && (
          <div className="benchmark-video-dialog__center">
            <CircularProgress size={28} />
            <p>{t("benchmarkProbing", "Checking saved calibration…")}</p>
          </div>
        )}

        {probe?.calibration && phase !== "probe" && (
          <div className="benchmark-video-dialog__calib">
            <strong>{t("benchmarkCalib", "Calibration")}</strong>
            <ul>
              <li>
                Source job:{" "}
                <code>{probe.calibration.source_job_id?.slice(0, 8)}…</code> (
                {probe.calibration.match})
              </li>
              <li>
                ROI L/R: {probe.calibration.has_roi_left ? "yes" : "no"} /{" "}
                {probe.calibration.has_roi_right ? "yes" : "no"}
              </li>
              <li>
                Table corners:{" "}
                {probe.calibration.has_table_corners ? "yes" : "no"}
              </li>
            </ul>
          </div>
        )}

        {phase === "ready" && (
          <p className="benchmark-video-dialog__hint">
            {t(
              "benchmarkReadyHint",
              "Runs MediaPipe (CPU), RTMPose (CUDA), and YOLO11-Pose (CUDA) with this video’s frozen ROIs / view / table geometry on the GCP L4 worker, then compares SmartTT stroke metrics and processing time."
            )}
            {probe?.gpu_agent === false ? (
              <span className="benchmark-video-dialog__warn">
                {" "}
                {t(
                  "benchmarkNoGpuAgent",
                  "POSE_GPU_BENCH_URL not set — CUDA jobs wait for remote_worker on the L4 VM."
                )}
              </span>
            ) : null}
          </p>
        )}

        {phase === "running" && (
          <div className="benchmark-video-dialog__center">
            <CircularProgress
              variant={progress != null ? "determinate" : "indeterminate"}
              value={Math.round((Number(progress) || 0) * 100)}
              size={36}
            />
            <p>
              {progressLabel || t("benchmarkRunning", "Running benchmark…")}{" "}
              {progress != null
                ? `${Math.round(Number(progress) * 100)}%`
                : ""}
            </p>
          </div>
        )}

        {error && (
          <div className="benchmark-video-dialog__error" role="alert">
            {error}
          </div>
        )}

        {(phase === "done" || (bench && phase === "running")) && (
          <div className="benchmark-video-dialog__table-wrap">
            <table className="benchmark-video-dialog__table">
              <thead>
                <tr>
                  <th>{t("metric", "Metric")}</th>
                  {modelCols.map((c) => (
                    <th key={c.id}>
                      {c.label}
                      <span
                        className={`benchmark-video-dialog__pill benchmark-video-dialog__pill--${c.status || "ready"}`}
                      >
                        {c.status || "ready"}
                      </span>
                      {c.error ? (
                        <div className="benchmark-video-dialog__col-err">{c.error}</div>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRIC_ROWS.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    {modelCols.map((c) => (
                      <td key={c.id}>
                        {c.status === "planned"
                          ? "—"
                          : formatMetric(c.metrics?.[row.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={phase === "running"}>
          {t("close", "Close")}
        </Button>
        {phase === "ready" || phase === "error" ? (
          <Button
            variant="contained"
            onClick={run}
            disabled={!probe?.calibration_ready}
          >
            {t("runBenchmark", "Run benchmark")}
          </Button>
        ) : null}
        {phase === "done" ? (
          <Button variant="contained" onClick={run}>
            {t("runAgain", "Run again")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export default BenchmarkVideoDialog;
